import { useState, useEffect, useMemo, useCallback } from 'react'
import { getContratosUsuario, getAtributosUsuario } from '@/common/utils/services/usuarios.service'
import { getIdUsuario } from '@/auth/auth.utils'

// Atributos que indican que el usuario no debe ver "Mis cuentas"
const ATRIBUTO_ADMIN_TOTAL = 'ADMINTOTAL'
const ATRIBUTO_ADMIN_HYS = 'ADMINHYS'
const VALOR_SI = 'S'

/**
 * Verifica si el usuario es Admin ART o Admin HYS basado en sus atributos
 * @param {Array} atributos - Lista de atributos del usuario
 * @returns {boolean} true si es Admin ART o Admin HYS
 */
const verificarEsAdminARToHyS = (atributos) => {
  if (!Array.isArray(atributos) || atributos.length === 0) {
    return false
  }
  return atributos.some(
    (attr) =>
      (attr.atributo === ATRIBUTO_ADMIN_TOTAL && attr.valor === VALOR_SI) ||
      (attr.atributo === ATRIBUTO_ADMIN_HYS && attr.valor === VALOR_SI)
  )
}

/**
 * Hook para obtener y gestionar las cuentas (contratos + solicitudes) del usuario
 * @returns {Object} Estado y funciones para la grilla de cuentas
 */
export const useCuentasUsuario = () => {
  const [contratos, setContratos] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  // null = aún no se conoce el perfil; true/false = ya se resolvió
  const [esAdminARToHyS, setEsAdminARToHyS] = useState(null)

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Cargar contratos + atributos (reutilizable para refrescar la grilla)
  const fetchDatos = useCallback(async () => {
    const idUsuario = getIdUsuario()
    if (!idUsuario) {
      setError('No se encontró el ID de usuario')
      return
    }

    setIsLoading(true)
    setError(null)

    // Obtener contratos y atributos en paralelo
    const [resultContratos, resultAtributos] = await Promise.all([
      getContratosUsuario(Number(idUsuario)),
      getAtributosUsuario(Number(idUsuario)),
    ])

    // Procesar contratos
    if (resultContratos.code === 200) {
      setContratos(resultContratos.data)
    } else {
      setError(resultContratos.message)
      setContratos([])
    }

    // Procesar atributos para determinar si es Admin ART o HYS
    if (resultAtributos.code === 200) {
      const esAdmin = verificarEsAdminARToHyS(resultAtributos.data)
      setEsAdminARToHyS(esAdmin)
    } else {
      // Si falla la consulta de atributos, se muestra la sección por defecto
      setEsAdminARToHyS(false)
    }

    setIsLoading(false)
  }, [])

  // Cargar datos al montar
  useEffect(() => {
    fetchDatos()
  }, [fetchDatos])

  // Filtrar por búsqueda
  const contratosFiltrados = useMemo(() => {
    if (!searchValue.trim()) return contratos
    const searchLower = searchValue.toLowerCase()
    return contratos.filter(
      (c) =>
        c.razonSocial?.toLowerCase().includes(searchLower) ||
        c.cuit?.toLowerCase().includes(searchLower) ||
        String(c.contrato).includes(searchLower)
    )
  }, [contratos, searchValue])

  // Resetear página al buscar
  useEffect(() => {
    setCurrentPage(1)
  }, [searchValue])

  // Paginación
  const datosPaginados = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return contratosFiltrados.slice(startIndex, endIndex)
  }, [contratosFiltrados, currentPage, pageSize])

  const headers = useMemo(() => ({ 'x-total-count': contratosFiltrados.length }), [contratosFiltrados])

  const handlePageChange = useCallback(
    ({ page, pageSize: newPageSize }) => {
      if (newPageSize && newPageSize !== pageSize) {
        setPageSize(newPageSize)
        setCurrentPage(1)
      } else if (page) {
        setCurrentPage(page)
      }
    },
    [pageSize]
  )

  return {
    contratos: datosPaginados,
    headers,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    onPageChange: handlePageChange,
    esAdminARToHyS,
    totalElementos: contratosFiltrados.length,
    refreshContratos: fetchDatos,
  }
}
